/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useLazyQuery, useMutation } from "@apollo/client";
import { useState, useEffect, useCallback, useRef } from "react";
import { TouchableOpacity, View } from "react-native";
import Icon from "react-icons-kit";
import { ic_keyboard_arrow_left } from "react-icons-kit/md/ic_keyboard_arrow_left";
import { useLocation } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { asyncCallApi, callApi } from "config/UpduoService";
import { homeResetState, _handlePrimaryMemberLogin } from "config/ResetState";
import {
  Alert,
  FadeInOutView,
  LoadingDots,
  Text,
  useTimerRef,
} from "UpduoLibrary";
import {
  skaleWhite,
  skaleWebBackgroundGray,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import {
  getNodes,
  getSummitState,
  sortByKey,
  SummitState,
} from "config/Utilities";
import {
  GET_PRIMARY_MEMBER_LIST,
  GET_SUMMIT_DETAIL,
  GET_USER_SUMMIT_SESSION_LIST,
} from "graphql/internal/Queries";
import { SkalePrimaryApolloClient } from "config/UpduoApolloClient";
import {
  GetPrimaryMemberListQuery,
  GetSummitDetailQuery,
  GetUserSessionListQuery,
  PrimaryJoinOrgResponse,
  SummitType,
} from "graphql/internal/generated";
import { PRIMARY_JOIN_ORG } from "graphql/internal/Mutations";
import { getSummitBackgroundColor } from "components/SummitPreview/utils";
import SummitPreview from "components/SummitPreview";
import IntroVideoModal from "./IntroVideoModal";
import * as UpduoAnalytics from "config/UpduoAnalytics";

interface SummitProps {
  startSessionButtonDisabled?: boolean;
  startSessionButtonPressed?: boolean;
  onPress: () => void;
  onBackPress: () => void;
  hashedId: string;
  inviteUuid?: string;
  preProcessingInProgress: boolean;
}

export default function Summit({
  startSessionButtonDisabled = false,
  startSessionButtonPressed,
  onPress,
  onBackPress,
  hashedId,
  inviteUuid,
  preProcessingInProgress,
}: SummitProps) {
  const dispatch = useDispatch();
  const location = useLocation();

  const selfObject = useSelector((state: RootState) => state.selfObject);
  const primaryAuthToken = useSelector(
    (state: RootState) => state.primaryAuthToken
  );
  const authToken = useSelector((state: RootState) => state.authToken);
  const primaryMemberList = useSelector(
    (state: RootState) => state.primaryMemberList
  );
  const autoJoinSummit = useSelector(
    (state: RootState) => state.autoJoinSummit
  );
  const selfObjectRef = useRef<any>(); // TODO: this should be SelfObject but it's incomplete
  selfObjectRef.current = selfObject;

  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );
  const [completedSkillIds, setCompletedSkillIds] = useState<any>(new Set());
  const [userSummitSessionList, setUserSummitSessionList] = useState<any[]>([]);
  const hasExitedSummit = useSelector(
    (state: RootState) => state.hasExitedSummit
  );

  const [summitHasError, setSummitHasError] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [summitHasEnded, setSummitHasEnded] = useState(false);
  const [summitHasNotStarted, setSummitHasNotStarted] = useState(false);
  const [hasSpecialAccess, setHasSpecialAccess] = useState(false);
  const [startDateObject, setStartDateObject] = useState<Date>();
  const [endDateObject, setEndDateObject] = useState<Date>();
  const [introVideoModalVisible, setIntroVideoModalVisible] = useState(false);

  const isDefaultSummitView =
    selfObject.organizationCode === selectedSummit?.organization?.orgCode;
  const [hasJoinedRsvpOrg, setHasJoinedRsvpOrg] = useState(false);
  const [rsvpSummitOrg, setRsvpSummitOrg] = useState("");

  const [setSummitStatusTimer, clearSummitStatusTimer] = useTimerRef();

  const [primaryJoinOrg, {}] = useMutation<PrimaryJoinOrgResponse>(
    PRIMARY_JOIN_ORG,
    {
      fetchPolicy: "no-cache", // this line is needed in order to proceed
      client: SkalePrimaryApolloClient,
      onCompleted: async ({ ok, id, reason }) => {
        if (!ok && reason) {
          UpduoAnalytics.logEvent("app-summit-joinorg-error", {
            reason,
          });
          Alert(
            "Error trying to join organization.",
            "You will be redirected to the home page.",
            [
              {
                text: reason,
                onPress: () => {
                  homeResetState();
                },
              },
            ]
          );
        } else {
          if (authToken) {
            await asyncCallApi("postLogoutMember");
          }
          if (selfObject.userId !== id) {
            getPrimaryMemberList({
              variables: {
                primaryMemberUserList: true,
              },
            });
            await _handlePrimaryMemberLogin(id!);
          }
          dispatch(ActionCreators.setShowRsvpModal(true));
          setHasJoinedRsvpOrg(true);
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-joinorganization-primaryjoinorg-error", {
          error,
        });
        console.error("ERROR primaryJoinOrg", error);
      },
    }
  );

  const [getSummitDetail, { error: getSummitDetailError }] =
    useLazyQuery<GetSummitDetailQuery>(GET_SUMMIT_DETAIL, {
      onCompleted: ({ userSummit }) => {
        dispatch(ActionCreators.setSelectedSummit(userSummit as SummitType));
        const newStartDateObject = userSummit?.startDate
          ? new Date(Date.parse(userSummit.startDate))
          : undefined;
        const newEndDateObject = userSummit?.endDate
          ? new Date(Date.parse(userSummit.endDate))
          : undefined;
        setStartDateObject(newStartDateObject);
        setEndDateObject(newEndDateObject);
      },
    });

  const [getUserSummitSessionList] = useLazyQuery<GetUserSessionListQuery>(
    GET_USER_SUMMIT_SESSION_LIST,
    {
      fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
      onCompleted: (result) => {
        if (result?.userSessions) {
          setUserSummitSessionList(getNodes(result.userSessions));
        }
      },
      onError: (error) => {
        console.error("ERROR getUserSummitSessionList", error);
        UpduoAnalytics.logEvent("app-summit-getusersummitsessionlist-error", {
          error,
        });
      },
    }
  );

  const [getPrimaryMemberList, { data: getPrimaryMemberListData }] =
    useLazyQuery<GetPrimaryMemberListQuery>(GET_PRIMARY_MEMBER_LIST, {});

  useEffect(() => {
    if (getPrimaryMemberListData) {
      dispatch(ActionCreators.setPrimaryMemberList(primaryMemberList));
    }
  }, [primaryMemberList]);

  useEffect(() => {
    if (primaryAuthToken && primaryMemberList) {
      if (primaryMemberList.length > 0) {
        setHasJoinedRsvpOrg(
          primaryMemberList.find(
            (item) => item.organization.orgCode === rsvpSummitOrg
          )
            ? true
            : false
        );
      }
    }
  }, [rsvpSummitOrg, primaryAuthToken, primaryMemberList]);

  useEffect(() => {
    if (location.search === "?rsvpSuccess=true") {
      dispatch(ActionCreators.setShowRsvpModal(true));
    }
  }, []);

  useEffect(() => {
    if (selectedSummit?.id) {
      setVideoUrl(selectedSummit?.introVideoUrl || "");
    }
  }, [summitHasEnded, selectedSummit]);

  useEffect(() => {
    const allowAsStaff =
      selfObjectRef.current.isStaff &&
      selfObjectRef.current.organizationIsOpenStaff;
    const allowAsTrainer =
      selfObjectRef.current.isTrainer &&
      selfObjectRef.current.organizationIsOpenTrainer;
    const allowAsMaintenance =
      selfObjectRef.current.isMaintenance &&
      selfObjectRef.current.organizationIsOpenMaintenance;
    const canGrantSpecialAccess =
      allowAsStaff || allowAsTrainer || allowAsMaintenance;
    setHasSpecialAccess(summitHasNotStarted && canGrantSpecialAccess);
  }, [summitHasNotStarted]);

  const _handleUpdateSummitStatus = useCallback(() => {
    if (startDateObject && endDateObject) {
      const now = new Date();
      const summitState = getSummitState({
        now: now,
        startDate: startDateObject,
        endDate: endDateObject,
      });
      setSummitHasEnded(summitState === SummitState.COMPLETED);
      setSummitHasNotStarted(summitState === SummitState.NOT_STARTED);
    }
  }, [startDateObject, endDateObject]);

  const _handleGetSummitDetail = useCallback(() => {
    // Retrieve skill list for Skills tab
    // Get the skill list w/ skill names and mastery levels from the server
    // TODO: Only list skills for current summit
    if (hashedId === "current") {
      return;
    }
    if (inviteUuid) {
      callApi(
        "getSummitDetailWithCode",
        undefined,
        (responseJson: any) => {
          dispatch(ActionCreators.setSelectedSummit(responseJson));
          const newStartDateObject = new Date(
            Date.parse(responseJson.start_date)
          );
          const newEndDateObject = new Date(Date.parse(responseJson.end_date));
          setStartDateObject(newStartDateObject);
          setEndDateObject(newEndDateObject);
          const newSummitSkillList =
            responseJson.skill_module.published_skill_list;
          dispatch(ActionCreators.setSummitSkillList(newSummitSkillList));
          setRsvpSummitOrg(responseJson.organization.org_code);
        },
        (error) => {
          setSummitHasError(true);
          UpduoAnalytics.logEvent("app-summit-getsummitdetailwithcode-error", {
            error,
          });
        },
        inviteUuid as any // TODO: callApi should change to accommodate this
      );
    } else {
      getSummitDetail({
        variables: { getSummitRequest: { summitId: hashedId } },
      });
    }
  }, [hashedId]);

  useEffect(() => {
    if (completedSkillIds.size < 1 && selectedSummit?.id && !inviteUuid) {
      const newCompletedSkillIds = new Set();
      for (let i = 0; i < userSummitSessionList.length; i++) {
        newCompletedSkillIds.add(userSummitSessionList[i].skill.id);
      }
      setCompletedSkillIds(newCompletedSkillIds);
      const newSummitSkillList = selectedSummit
        ? selectedSummit.skillModules
            .map((skillModule) => getNodes(skillModule.activeSkillList))
            .flat()
            .filter(
              (item) => item.isPublished || newCompletedSkillIds.has(item.id)
            )
        : [];
      sortByKey(newSummitSkillList, "isPublished").reverse();

      dispatch(ActionCreators.setSummitSkillList(newSummitSkillList));
    }
  }, [userSummitSessionList]);

  const _handleProceed = useCallback(
    (summitId) => {
      const shouldProceedToSession =
        selfObject.organizationIsOpen || hasSpecialAccess;
      if (shouldProceedToSession) {
        onPress();
      }
      hasExitedSummit[summitId] = false;
      dispatch(ActionCreators.setHasExitedSummit(hasExitedSummit));
    },
    [selfObject.organizationIsOpen, hasSpecialAccess, onPress]
  );

  useEffect(() => {
    _handleGetSummitDetail();
    return () => {
      clearSummitStatusTimer();
    };
  }, [_handleGetSummitDetail]);

  useEffect(() => {
    if (selectedSummit?.id) {
      _handleUpdateSummitStatus();
      setSummitStatusTimer(
        setInterval(() => {
          _handleUpdateSummitStatus();
        }, 1000)
      );
      if (isDefaultSummitView && selectedSummit?.id) {
        getUserSummitSessionList({
          variables: {
            getUserSummitSessionListRequest: {
              summitId: selectedSummit?.id,
            },
          },
        });
      }
    }
  }, [selectedSummit, _handleUpdateSummitStatus]);

  const _handleConnect = useCallback(() => {
    if (primaryAuthToken && !hasJoinedRsvpOrg && summitHasNotStarted) {
      primaryJoinOrg({
        variables: {
          primaryJoinOrgRequest: {
            orgCode: rsvpSummitOrg,
          },
        },
      });
    }

    const hasVideo = videoUrl !== "";
    if (hasVideo && isDefaultSummitView) {
      setIntroVideoModalVisible(true);
    } else {
      _handleProceed(selectedSummit?.id);
    }
  }, [
    primaryAuthToken,
    hasJoinedRsvpOrg,
    summitHasNotStarted,
    rsvpSummitOrg,
    videoUrl,
    isDefaultSummitView,
    _handleProceed,
    selectedSummit,
  ]);

  const doPreventQueueReentry =
    (startSessionButtonDisabled || summitHasNotStarted) &&
    !hasSpecialAccess &&
    Boolean(selfObject.organizationStartButtonText);

  const joinSummitDisabled =
    summitHasNotStarted && rsvpSummitOrg && !hasJoinedRsvpOrg
      ? false
      : doPreventQueueReentry;

  useEffect(() => {
    if (
      autoJoinSummit &&
      !preProcessingInProgress &&
      !startSessionButtonPressed &&
      !joinSummitDisabled
    ) {
      _handleConnect();
    }
    // else, we may be unable to auto join summit
    // maybe we should reset autoJoinSummit eventually here
  }, [
    joinSummitDisabled,
    _handleConnect,
    autoJoinSummit,
    startSessionButtonPressed,
    preProcessingInProgress,
  ]);

  if (!selectedSummit?.id) {
    return summitHasError || getSummitDetailError ? (
      <View style={{ flex: 1, padding: 50, alignItems: "center" }}>
        <Text isHeading={true}>
          {"Event not found. Please check your URL."}
        </Text>
      </View>
    ) : (
      <View />
    );
  }

  if (autoJoinSummit) {
    return (
      <div
        style={{
          backgroundColor: skaleWhite,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        className={"fill-height-except-navbar"}
      >
        <LoadingDots isAlternate={true} />
        <IntroVideoModal
          visible={introVideoModalVisible}
          onClose={() => {
            setIntroVideoModalVisible(false);
          }}
          disableProceedToSession={doPreventQueueReentry}
          onProceedToSession={() => {
            _handleProceed(selectedSummit?.id);
          }}
          videoUrl={videoUrl}
        />
      </div>
    );
  }

  const backgroundColor = getSummitBackgroundColor(selectedSummit);

  return (
    <div
      style={{
        backgroundColor: backgroundColor,
        paddingLeft: 60,
        paddingRight: 60,
        paddingTop: 32,
        paddingBottom: 52,
        boxSizing: "border-box",
      }}
      className={"fill-height-except-navbar"}
    >
      <FadeInOutView
        style={{
          padding: 0,
          backgroundColor: skaleWhite,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: skaleWebBackgroundGray,
          minHeight: 450,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TouchableOpacity
          onPress={onBackPress}
          style={{
            paddingHorizontal: 32,
            paddingTop: 24,
            paddingBottom: 16,
          }}
        >
          <Text style={{ color: upduoPrimaryBlack }}>
            <Icon
              icon={ic_keyboard_arrow_left}
              size={28}
              style={{ verticalAlign: "middle" }}
            />
            <Text style={{ verticalAlign: "middle" } as any}>
              {"Back to Events"}
            </Text>
          </Text>
        </TouchableOpacity>
        <div
          style={{
            width: "80%",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            flexShrink: 1,
            alignSelf: "center",
            minWidth: 700,
            maxWidth: 1300,
            paddingBottom: 57,
            minHeight: 0,
            boxSizing: "border-box",
          }}
        >
          <SummitPreview
            summit={selectedSummit}
            onConnect={_handleConnect}
            connectButtonDisabled={joinSummitDisabled}
            connectButtonShowSpinner={startSessionButtonPressed}
          />
        </div>
      </FadeInOutView>
      <IntroVideoModal
        visible={introVideoModalVisible}
        onClose={() => {
          setIntroVideoModalVisible(false);
        }}
        disableProceedToSession={doPreventQueueReentry}
        onProceedToSession={() => {
          _handleProceed(selectedSummit?.id);
        }}
        videoUrl={videoUrl}
      />
    </div>
  );
}
